import { CSSProperties, FC, ReactNode, useMemo } from 'react'

import { Breakpoints, theme } from '@hhgtech/hhg-components'
import { ArticleSvg } from '@hhgtech/icons/together'
import {
  ActionIcon,
  Box,
  Flex,
  rem,
  Stack,
  useMantineTheme,
} from '@mantine/core'
import innertext from 'innertext'
import { useIntl } from 'react-intl'

import { formatArticle } from 'api/dataTransform'
import { getHelloSitesUrl } from 'api/utils/paths'
import ChevronRightSvg from 'components/icons/ChevronRightSvg'
import FeatureCard from 'components/molecules/FeatureCard'
import { FeatureCardLayout } from 'components/molecules/FeatureCard/types'
import { useBookmark } from 'hooks/useBookmark'
import { useIsMobile } from 'hooks/useIsMobile'
import { useRouterExtend } from 'hooks/useRouterExtend'
import { basePath } from 'interfaces/constants'
import { ArticleApi } from 'interfaces/types'
import { getAuthorFromArticleApi } from 'utils/author'

import {
  StyledHomeFeature,
  StyledHomeFeatureBox,
  StyledHomeFeatureBoxArticle,
  StyledHomeFeatureBoxBody,
  StyledHomeFeatureBoxHeader,
  StyledHomeFeatureBoxTitle,
  StyledHomeFeatureBoxTitleLink,
} from './HomeFeature.styled'
import { CardArticle } from '../CardArticleV2'
import { CardArticleAuthorName } from '../CardArticleV2/CardArticleAuthor'
import CarouselCard from '../CarouselCardV2'
import GroupItems from '../groupItems'

export type HomeFeatureItem = {
  posts: ArticleApi[]
  category: {
    term_id: number
    name: string
    permalink: string
    text_color: string
  }
}

export type HomeFeatureProps = {
  items?: HomeFeatureItem[]
  sideComponent?: ReactNode
  className?: string
  style?: CSSProperties
}

export const HomeFeature: FC<HomeFeatureProps> = (props) => {
  const { items = [], sideComponent, className, style } = props
  const { locale } = useRouterExtend()
  const { formatMessage: f } = useIntl()
  const { bookmarks, loadingBookmark, onClickBookmark } = useBookmark()
  const currentDomain = useMemo(() => getHelloSitesUrl(locale), [locale])
  const isMobile = useIsMobile()
  const mantineTheme = useMantineTheme()

  return (
    <>
      <FeatureCard
        layout={FeatureCardLayout.VerticalCenter}
        sx={(theme) => ({
          [theme.fn.smallerThan('sm')]: {
            backgroundColor: '#fff',
          },
        })}
      >
        <FeatureCard.Heading>
          {f({
            id: 'home.featureArticle.heading',
            defaultMessage: 'Các bài viết dành riêng cho bạn',
          })}
        </FeatureCard.Heading>
        <FeatureCard.SectionName
          sx={(theme) => ({
            '.section-icon': {
              path: {
                stroke: theme.fn.primaryColor(),
              },
            },
          })}
          icon={
            <ArticleSvg
              size={16}
              color={mantineTheme.fn.primaryColor()}
              className="section-icon"
            />
          }
        >
          {f({
            id: 'home.featureArticle.sectionName',
            defaultMessage: 'Articles',
          })}
        </FeatureCard.SectionName>
        <FeatureCard.Content>
          <StyledHomeFeature className={className} style={style}>
            <Flex
              wrap="wrap"
              align="stretch"
              justify="center"
              columnGap={{ 320: 0, lg: rem(24) }}
            >
              <Box
                sx={({ breakpoints }) => ({
                  [`@media (min-width: ${rem(320)})`]: {
                    width: '100%',
                  },
                  [`@media (min-width: ${breakpoints.lg})`]: {
                    flex: 1,
                    minWidth: 0,
                  },
                })}
              >
                <CarouselCard
                  slideSize={items.length === 1 ? '100%' : '50%'}
                  slideGap={rem(24)}
                  slidesToScroll={isMobile ? 1 : 2}
                  withControls={false}
                  autoplay
                  breakpoints={[
                    {
                      maxWidth: Breakpoints.BREAK_POINT_TABLET,
                      slideSize: items.length === 1 ? '100%' : '80%',
                      slideGap: rem(16),
                    },
                  ]}
                  styles={(theme) => ({
                    viewport: {
                      [theme.fn.smallerThan('sm')]: {
                        paddingLeft: 16,
                        paddingRight: 16,
                      },
                    },
                  })}
                >
                  <Stack spacing={24}>
                    <CarouselCard.Body>
                      <GroupItems
                        itemsPerGroup={1}
                        GroupWrapper={(children) => (
                          <CarouselCard.Slide>{children}</CarouselCard.Slide>
                        )}
                        items={items.map((item, index) => (
                          <StyledHomeFeatureBox
                            className="featureBox"
                            key={String(index)}
                          >
                            <StyledHomeFeatureBoxHeader className="featureBoxHeader">
                              <StyledHomeFeatureBoxTitleLink
                                href={item.category?.permalink}
                                className="featureBoxTitleLink"
                                data-event-category="Recommend Category"
                                data-event-action="See More Categories Dropdown"
                                data-event-label={`${currentDomain}${basePath}${item.category?.permalink}`}
                              >
                                <StyledHomeFeatureBoxTitle className="featureBoxTitle">
                                  {item.category?.name}
                                </StyledHomeFeatureBoxTitle>
                                <ActionIcon
                                  size={rem(24)}
                                  radius="xl"
                                  bg={theme.colors.blue50}
                                  color={theme.colors.primaryBase}
                                  className="featureBoxTitleIcon"
                                >
                                  <ChevronRightSvg />
                                </ActionIcon>
                              </StyledHomeFeatureBoxTitleLink>
                            </StyledHomeFeatureBoxHeader>
                            <StyledHomeFeatureBoxBody className="featureBoxBody">
                              {item.posts?.map((post, index) => {
                                const article = formatArticle(post)
                                const author = getAuthorFromArticleApi({
                                  article: post,
                                  locale,
                                  version: 'short',
                                })

                                return (
                                  <StyledHomeFeatureBoxArticle key={`${index}`}>
                                    <CardArticle
                                      type="horizontalFooter"
                                      item={{
                                        ID: parseInt(`${article.id}`),
                                        image: article.featureImage,
                                        image1x1:
                                          article.featureImageWithRatio1x1,
                                        permalink: article.permalink,
                                        title: innertext(
                                          article.postTitle ||
                                            article.postName ||
                                            '',
                                        ),
                                        category: article.category,
                                        author: {
                                          name: (
                                            <CardArticleAuthorName
                                              {...author}
                                              formatOptionsProps={{
                                                expert_display_name: {
                                                  'data-event-category':
                                                    'Recommend Category',
                                                  'data-event-action':
                                                    'See Doctor',
                                                  'data-event-label': `${currentDomain}${basePath}${author.avatar_permalink}`,
                                                },
                                              }}
                                            />
                                          ),
                                          avatar: author.avatar,
                                          avatarAlt: author.avatar_alt,
                                          permalink: author.avatar_permalink,
                                          indicator: author.indicator,
                                        },
                                      }}
                                      itemTracking={{
                                        image: {
                                          'data-event-category':
                                            'Recommend Category',
                                          'data-event-action':
                                            'Category Article Title',
                                          'data-event-label': `${currentDomain}${basePath}${article.permalinkDecode}`,
                                        },
                                        title: {
                                          'data-event-category':
                                            'Recommend Category',
                                          'data-event-action':
                                            'Category Article Title',
                                          'data-event-label': `${currentDomain}${basePath}${article.permalinkDecode}`,
                                        },
                                        category: {
                                          'data-event-category':
                                            'Recommend Category',
                                          'data-event-action': 'Category Title',
                                          'data-event-label': `${currentDomain}${basePath}${article.category.permalinkDecode}`,
                                        },
                                        authorAvatar: {
                                          'data-event-category':
                                            'Recommend Category',
                                          'data-event-action': 'See Doctor',
                                          'data-event-label': `${currentDomain}${basePath}${author.avatar_permalink}`,
                                        },
                                      }}
                                      bookmarkButtonProps={{
                                        loading: !!loadingBookmark[article.id],
                                      }}
                                      isBookmark={
                                        !!bookmarks.includes(article.id)
                                      }
                                      onClickBookmark={onClickBookmark(
                                        parseInt(`${article.id}`),
                                      )}
                                    />
                                  </StyledHomeFeatureBoxArticle>
                                )
                              })}
                            </StyledHomeFeatureBoxBody>
                          </StyledHomeFeatureBox>
                        ))}
                      />
                    </CarouselCard.Body>
                    <CarouselCard.Footer>
                      <CarouselCard.Progress />
                      <CarouselCard.Controls>
                        <CarouselCard.Control
                          direction="prev"
                          data-event-category="Recommend Category"
                          data-event-action=" See More Article"
                          data-event-label={`${currentDomain}${basePath}`}
                          color="gray"
                        />
                        <CarouselCard.Control
                          direction="next"
                          data-event-category={'Recommend Category'}
                          data-event-action="See More Article"
                          data-event-label={`${currentDomain}${basePath}`}
                          color="gray"
                        />
                      </CarouselCard.Controls>
                    </CarouselCard.Footer>
                  </Stack>
                </CarouselCard>
              </Box>
              <Box
                sx={({ breakpoints }) => ({
                  [`@media (max-width: ${breakpoints.sm})`]: {
                    minWidth: '100%',
                    backgroundColor: theme.colors.neutral50,
                  },
                  [`@media (min-width: ${breakpoints.lg})`]: {
                    minWidth: rem(300),
                  },
                })}
              >
                {sideComponent}
              </Box>
            </Flex>
          </StyledHomeFeature>
        </FeatureCard.Content>
      </FeatureCard>
    </>
  )
}
